import { zodResolver } from "@hookform/resolvers/zod";
import { accountCategoryOptions, formatAmount, getRequiredFields } from "@/lib/utils";
import { routes } from "@/routes/utils";
import { useGetTrialBalanceLineItemReportExport, useGetTrialBalanceReportExport, useGetOwnerTrialBalanceLineItemReport, useGetOwnerTrialBalanceReport, useGetPropertyOccupancyListByCommunity, useGetGlMasterData } from "@/services";
import { RootState } from "@/redux/store";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  CLEARING_STATUS,
  COMMUNITY_ID,
  DATE_RANGE,
  lineItemColumnOptions,
  MONTHLY,
  SELECTION_CRITERIA,
  selectionCriteriaOptions,
  OWNER,
  YEAR,
  GL_MASTER_IDS,
} from "./consts";
import { formValues, schema } from "./validations";
import moment from "moment";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";

const useOwnerTrialBalance = () => {
  const {communityData} = useSelector((state: RootState) => state.currentData);

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      [SELECTION_CRITERIA]: MONTHLY,
      [DATE_RANGE]: {
        from: undefined,
        to: undefined,
      },
      [YEAR]: "",
      [OWNER]: [],
      [CLEARING_STATUS]: "ALL",
      [GL_MASTER_IDS]: []
    },
  });
  const queryClient = useQueryClient();
  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  const [isRunClicked, setIsRunClicked] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [openColumnDrawer, setOpenColumnDrawer] = useState(false);
  const [openDocumentDialog, setOpenDocumentDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedLineItemColumns, setSelectedLineItemColumns] =
    useState(lineItemColumnOptions);
  const [drawerLineItemColumns, setDrawerLineItemColumns] =
    useState(lineItemColumnOptions);
    
  const communityId = communityData?.communityId;
  const selectionCriteria = form.watch(SELECTION_CRITERIA) || "monthly";
  const dateRange = useWatch({ control: form.control, name: DATE_RANGE });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityData?.name || ""}`,
      },
      {
        link: "",
        name: <span className="text-primary">Owner Trial Balance</span>,
      },
    ],
    []
  );
  const requiredFields = getRequiredFields(schema);

  useEffect(() => {
    queryClient.removeQueries({queryKey: ["vendor-trial-balance-report"]})
    queryClient.removeQueries({queryKey: ["trial-balance-report-export"]})
    queryClient.removeQueries({queryKey: ["vendor-trial-balance-line-item-report"]})
    queryClient.removeQueries({queryKey: ["trial-balance-line-item-report-export"]})
  }, []);

  const visibleLineItemColumns = selectedLineItemColumns.filter(
    (column) => column.checked
  );

  const onChangeLineItemColumn = (key: string, checked: boolean) => {
    setDrawerLineItemColumns((prev) =>
      prev.map((column) =>
        column.key === key ? { ...column, checked } : column
      )
    );
  };

  const onApplyLineItemColumns = () => {
    setSelectedLineItemColumns([...drawerLineItemColumns]);
    setOpenColumnDrawer(false);
  };

  const onOpenAccountingDocument = (document: any) => {
    setSelectedDocument({ ...document, postDocHeaderId: document?.postDocumentHeaderId, communityId });
    setOpenDocumentDialog(true);
  };

  const reportColumns = [
    {
      accessorKey: "ownerName",
      header: "Owner Name",
      accessorFn: (row: any) =>
        `${row?.ownerName}` || "-",
    },
    {
      accessorKey: "description",
      header: "Description",
      accessorFn: (row: any) =>
        row?.description || "-",
    },
    {
      accessorKey: "accountCategory",
      header: "Category",
      accessorFn: (row: any) => row?.accountCategory ? 
        accountCategoryOptions.find(
          (val) => val.value == row?.accountCategory
        )?.label
      : "",
    },
    {
      accessorKey: "opening",
      meta: { align: "right" },
      header: "Opening",
      accessorFn: (row: any) =>
        `${formatAmount(row?.openingBalance || "0")} ${row?.openingDirection || ""}`.trim(),
    },
    {
      accessorKey: "totalDebits",
      meta: { align: "right" },
      header: "Debits",
      accessorFn: (row: any) => formatAmount(row?.totalDebits || 0),
    },
    {
      accessorKey: "totalCredits",
      meta: { align: "right" },
      header: "Credits",
      accessorFn: (row: any) => formatAmount(row?.totalCredits || 0),
    },
    {
      accessorKey: "closingBalance",
      meta: { align: "right" },
      header: "Closing",
      accessorFn: (row: any) => `${formatAmount(row?.closingBalance || "0")} ${row?.closingDirection}`,
    },
  ];

  const formData = useWatch({ control: form.control });

  const reportParams = useMemo(() => {
    return {
      [COMMUNITY_ID]: communityId,
      fromMonth: (() => {
        const month = formData[SELECTION_CRITERIA] == "monthly" ? moment(formData[DATE_RANGE]?.from).format('MM') : '00';
        return month === '01' ? 0 : month;
      })(),
      fromYear: formData[SELECTION_CRITERIA] == "monthly" ? moment(formData[DATE_RANGE]?.from).format('YYYY') : formData[YEAR],
      toMonth: formData[SELECTION_CRITERIA] == "monthly" ? moment(formData[DATE_RANGE]?.to).format('MM') : '12',
      toYear: formData[SELECTION_CRITERIA] == "monthly" ? moment(formData[DATE_RANGE]?.to).format('YYYY') : formData[YEAR],
      includePeriod0: true,
      [OWNER]: formData[OWNER] || [],
      [CLEARING_STATUS]: formData[CLEARING_STATUS] || "ALL",
      ...(formData[GL_MASTER_IDS]?.length ? { [GL_MASTER_IDS]: formData[GL_MASTER_IDS] } : {}),
    }
  }, [formData]);

  const {data: ownerList, isLoading: isOwnerLoading} = useGetPropertyOccupancyListByCommunity({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  });

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId,
    },
  });

  const glOptions = useMemo(() =>
    (glData?.data || [])
      .filter((data: any) => data?.accountCategory == "I")
      .map((data: any) => ({
        value: data?.glMasterId?.toString(),
        label: `${data?.glAccountNumber || ""} - ${data?.accountShortDesc || ""}`,
      })),
    [glData]
  );

  const {data: trialBalanceReportData, refetch: refetchTrialBalanceReport, isRefetching: isTrialBalanceLoading} = useGetOwnerTrialBalanceReport({
    filter: reportParams,
    queryParams: {
      enabled: false
    }
  });

  const {data: trialBalanceReportExport, refetch: refetchTrialBalanceReportExport, isRefetching: isTrialBalanceExporting} = useGetTrialBalanceReportExport({
    type: "owner",
    filter: {...reportParams, includeLineItems: true},
    queryParams: {
      enabled: false
    }
  });

  const {data: lineItemData, refetch: refetchLineItemData, isRefetching: isLineItemLoading} = useGetOwnerTrialBalanceLineItemReport({
    ownerId: selectedRow?.ownershipId,
    filter: reportParams,
    queryParams: {
      enabled: false
    }
  });

  const {data: lineItemReportData, refetch: refetchLineItemReportData, isRefetching: isLineItemReportLoading} = useGetTrialBalanceLineItemReportExport({
    type: "owner",
    id: selectedRow?.ownershipId,
    filter: reportParams,
    queryParams: {
      enabled: false
    }
  });

  useEffect(() => {
    if (trialBalanceReportExport) {
      try {
        const response = trialBalanceReportExport
          // Create blob link and trigger download
          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
  
          // Try to get filename from headers, fallback to fileName or default
          const contentDisposition = response.headers["content-disposition"];
          let downloadFileName =  `${communityId}-vendor-trial-balance-report.xlsx`;
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match?.[1]) {
              downloadFileName = match[1];
            }
          }
  
          link.setAttribute("download", downloadFileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || "There is an issue while downloading the file"
        );
      } finally {
        queryClient.removeQueries({queryKey: ["trial-balance-report-export"]})
      }
    }
  }, [trialBalanceReportExport]);

  useEffect(() => {
    if (lineItemReportData) {
      try {
        const response = lineItemReportData
          // Create blob link and trigger download
          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
  
          // Try to get filename from headers, fallback to fileName or default
          const contentDisposition = response.headers["content-disposition"];
          let downloadFileName =  `${communityId}-vendor-trial-balance-line-item-report.xlsx`;
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match?.[1]) {
              downloadFileName = match[1];
            }
          }
  
          link.setAttribute("download", downloadFileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || "There is an issue while downloading the file"
        );
      } finally {
        queryClient.removeQueries({queryKey: ["trial-balance-line-item-report-export"]})
      }
    }
  }, [lineItemReportData]);

  const filteredReportData = useMemo(() => {
    const rawData = trialBalanceReportData?.data?.rows || [];
  
    if (!searchValue) return rawData;
  
    const searchLower = searchValue.toLowerCase();
  
    return rawData.filter((item: any) => {
      const accountNumber = item?.glAccountNumber?.toLowerCase() || '';
      const description = item?.description?.toLowerCase() || '';
      
      return accountNumber.includes(searchLower) || description.includes(searchLower);
    });
  }, [trialBalanceReportData, searchValue]);

  const onSubmit = () => {
    refetchTrialBalanceReport();
    setIsRunClicked(true);
  }

  const onDownloadExcel = () => {
    refetchTrialBalanceReportExport();
  }

  const totalDebits = useMemo(() => (trialBalanceReportData?.data?.totals?.totalDebits || 0), [trialBalanceReportData]);
  const totalCredits = useMemo(() => (trialBalanceReportData?.data?.totals?.totalCredits || 0), [trialBalanceReportData]);

  const isBalanced = totalDebits === totalCredits;

  const onExpandStateChange = (row: any, _expanded: boolean) => {
    setSelectedRow(row?.original);
    setTimeout(() => {
      refetchLineItemData();
    }, 100)
  }

  const onDownloadGlExcel = () => {
    refetchLineItemReportData();
  }

  const displayDate = useMemo(() => {
    if (trialBalanceReportData?.data) {
      const {fromMonth, fromYear, toMonth, toYear} = trialBalanceReportData?.data || {};
      const fromDate = `${Number(fromMonth) === 0 ? 1 : fromMonth}-01-${fromYear}`;
      const toDate = `${toMonth}-01-${toYear}`;
      return `${moment(fromDate).format('MM/YYYY')} - ${moment(toDate).format('MM/YYYY')}`
    }
  }, [trialBalanceReportData]);

  const onClear = () => {
    form.reset({
      [SELECTION_CRITERIA]: MONTHLY,
      [DATE_RANGE]: {
        from: undefined,
        to: undefined,
      },
      [YEAR]: "",
      [OWNER]: [],
      [CLEARING_STATUS]: "ALL",
      [GL_MASTER_IDS]: []
    });
    queryClient.removeQueries({queryKey: ["trial-balance-report"]})
  };

  const activeBadge = useMemo(() => {
    // Helper to format date range into "MMM YYYY - MMM YYYY" format string
    const formatDateRange = (dateRange: any) => {
      if (!dateRange?.from || !dateRange?.to) return "";
      const options: Intl.DateTimeFormatOptions = { month: "short", year: "numeric" };
      const fromStr = new Date(dateRange.from).toLocaleDateString("en-US", options);
      const toStr = new Date(dateRange.to).toLocaleDateString("en-US", options);
      return `${fromStr} - ${toStr}`;
    };

    // 1. Gather all active tags dynamically based on what is selected in formState
    const activeBadges = [];

    if (formData?.[SELECTION_CRITERIA]) {
      activeBadges.push({
        key: SELECTION_CRITERIA,
        label: selectionCriteriaOptions?.find((data: any) => data?.value == formData[SELECTION_CRITERIA])?.label,
        clearValue: "", // value to set on clear
        hasDefault: true
      });
    }

    if (formData[SELECTION_CRITERIA] == MONTHLY) {
      const dateRangeStr = formatDateRange(formData?.[DATE_RANGE]);
      if (dateRangeStr) {
        activeBadges.push({
          key: DATE_RANGE,
          label: dateRangeStr,
          clearValue: { from: undefined, to: undefined },
        });
      }
    } else {
      if (formData?.[YEAR]) {
        activeBadges.push({
          key: YEAR,
          label: formData[YEAR],
          clearValue: "", // value to set on clear
        });
      }
    }

    return activeBadges
  }, [formData]);

  useEffect(() => {
    if (formData[SELECTION_CRITERIA] == "MONTHLY") {
      form.setValue(YEAR, "")
    } else {
      form.setValue(DATE_RANGE, {
        from: undefined,
        to: undefined
      })
    }
  }, [formData[SELECTION_CRITERIA]])

  const onClearField = (key: any, value: any) => {
    form.setValue(key, value)
  }

  return {
    breadcrumbData,
    form,
    requiredFields,
    isTrialBalanceLoading,
    currentFinanceYear,
    reportColumns,
    selectionCriteria,
    onSubmit,
    trialBalanceReportData,
    totalDebits,
    totalCredits,
    dateRange,
    isBalanced,
    searchValue,
    setSearchValue,
    filteredReportData,
    setOpenColumnDrawer,
    openColumnDrawer,
    selectedLineItemColumns,
    visibleLineItemColumns,
    isTrialBalanceExporting,
    onDownloadExcel,
    onExpandStateChange,
    isLineItemLoading,
    lineItemData,
    onApplyLineItemColumns,
    onChangeLineItemColumn,
    drawerLineItemColumns,
    formData,
    isLineItemReportLoading,
    onDownloadGlExcel,
    onOpenAccountingDocument,
    setOpenDocumentDialog,
    openDocumentDialog,
    selectedDocument,
    isRunClicked,
    displayDate,
    communityData,
    onClear,
    activeBadge,
    onClearField,
    ownerList,
    isOwnerLoading,
    glOptions,
    isLoadingGlMaster
  };
};

export default useOwnerTrialBalance;
